import { PropsWithChildren, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "@atlaskit/button";
import Form from "@atlaskit/form";
import { N700, N800 } from "@atlaskit/theme/colors";

import { fontSize, getFirstName } from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { Jfind1315SubHeading } from "@/templates/try/cloud/signup/signup-page/src/logged-out/confluence/free/components/jfind-1315-subheading";

import { EulaText } from "../../../signup-form/components";
import { ButtonSubmit, InputSiteName } from "../../components";
import { useSiteNameContext } from "../../site-name-context";
import { InputEmail } from "../input-email";
import { SitePickerField } from "../site-picker-field";

const privacyPolicyCss = css({
    color: N800,
    fontSize: fontSize.xs,
    lineHeight: 1.5,
    margin: "0.5rem 0 1.5rem"
});

const containerCss = css({
    alignItems: "center",
    color: N700,
    display: "flex",
    flexDirection: "column"
});

const formWrapperCss = css({
    marginBlockTop: "0.75rem",
    width: "100%"
});

const logoWrapperCss = css({
    marginBlockEnd: "40px"
});

const legalTextCss = css({
    fontSize: fontSize.xs,
    lineHeight: "0.875rem",
    margin: "2rem 0 1rem !important"
});

const toggleSiteButtonCss = css({
    margin: "5px 0 0 0",
    textDecoration: "underline !important"
});

export const DefaultSiteNameLogo = () => {
    const { productData } = useSiteNameContext();
    const Logo = productData.productLogo;

    return !!Logo ? (
        <div css={logoWrapperCss}>
            <Logo appearance="brand" size="large" />
        </div>
    ) : null;
};

export const DefaultSiteNameHeader = () => {
    const {
        productData: { productName },
        userInfo: { name },
        isNewUser
    } = useSiteNameContext();
    const intl = useIntl();

    const headerText = isNewUser
        ? intl.formatMessage({
              id: "BxpUiSignup.nameSite.header.jiraPricingRebrand",
              defaultMessage: "Let’s name your site"
          })
        : intl.formatMessage(
              {
                  id: "BxpUiSignup.nameSite.header",
                  defaultMessage: "Welcome back, {name}"
              },
              {
                  name: getFirstName(name)
              }
          );

    const subHeaderText = isNewUser ? (
        intl.formatMessage(
            {
                id: "BxpUiSignup.nameSite.subHeader.newUser",
                defaultMessage:
                    "Your site name is part of your {product} URL. Most people use their team or company name."
            },
            {
                product: productName
            }
        )
    ) : (
        <Jfind1315SubHeading
            returnString={true}
            controlText={intl.formatMessage({
                id: "BxpUiSignup.nameSite.subHeader",
                defaultMessage:
                    "Free for up to 10 users - no credit card required."
            })}
        />
    );

    return (
        <>
            <h1>{headerText}</h1>
            <h2>{subHeaderText}</h2>
        </>
    );
};

export const DefaultSiteNameInput = () => {
    const {
        isNewUser,
        showSitePickerState: [showSitePicker, setShowSitePicker],
        productData: { productKey }
    } = useSiteNameContext();

    const { profile } = useUserContext();

    const intl = useIntl();
    const [hasAvailableSites, setHasAvailableSites] = useState(false);

    const toggleYourSiteButtonText = showSitePicker
        ? intl.formatMessage({
              id: "BxpUiSignup.nameSite.startNewSiteButton",
              defaultMessage: "Start a new site"
          })
        : intl.formatMessage({
              id: "BxpUiSignup.nameSite.useExistingSiteButton",
              defaultMessage: "Use an existing site"
          });

    useEffect(() => {
        if (!profile.requestInProgress) {
            const availableSites =
                profile.attributes?.existingSites.filter(
                    ({ products }) =>
                        !products.some(
                            (product) => product.productKey === productKey
                        )
                ) ?? [];
            setHasAvailableSites(availableSites.length > 0);
            setShowSitePicker(availableSites.length > 0);
        }
    }, [
        productKey,
        profile.attributes?.existingSites,
        profile.requestInProgress,
        setShowSitePicker
    ]);

    return isNewUser ? (
        <>
            <InputSiteName />
            <div css={privacyPolicyCss}>
                <FormattedMessage
                    id="BxpUiSignup.nameSite.inputSiteNameMessage"
                    defaultMessage="This site name is just a suggestion. Feel free to change to something your team will recognize."
                />
            </div>
        </>
    ) : (
        <>
            <InputEmail />
            {showSitePicker ? <SitePickerField /> : <InputSiteName />}
            {hasAvailableSites && (
                <Button
                    appearance="link"
                    spacing="none"
                    css={toggleSiteButtonCss}
                    onClick={() => setShowSitePicker(!showSitePicker)}
                >
                    {toggleYourSiteButtonText}
                </Button>
            )}

            <p css={legalTextCss}>
                <EulaText />
            </p>
        </>
    );
};

export const DefaultSiteNameForm = ({ children }: PropsWithChildren) => {
    const {
        activateSite,
        isSubmittingState: [isSubmitting],
        isNewUser
    } = useSiteNameContext();

    const intl = useIntl();
    const siteNameAgreeButton = isNewUser
        ? intl.formatMessage({
              id: "BxpUiSignup.nameSite.continueButton",
              defaultMessage: "Continue"
          })
        : intl.formatMessage({
              id: "BxpUiSignup.nameSite.agreeButton",
              defaultMessage: "Agree and start now"
          });

    return (
        <Form onSubmit={activateSite} isDisabled={false}>
            {({ formProps, submitting }) => (
                <form {...formProps} autoComplete="off">
                    {children}
                    <ButtonSubmit
                        data-testid="input-site-name-form-submit"
                        submitting={submitting}
                        hasSubmitted={isSubmitting}
                        formError={false}
                        submitButtonLabel={siteNameAgreeButton}
                    />
                </form>
            )}
        </Form>
    );
};

export const DefaultSiteName = () => {
    return (
        <div css={containerCss} data-testid="site-name-form">
            <DefaultSiteNameLogo />
            <DefaultSiteNameHeader />
            <DefaultSiteNameForm>
                <div css={formWrapperCss}>
                    <DefaultSiteNameInput />
                </div>
            </DefaultSiteNameForm>
        </div>
    );
};
